/**
 * CasePdfsPage.tsx
 * 「発行した書類のダウンロード画面」（案件単位）。
 * 書類の発行直後にこの画面へ遷移し（location.state.justIssuedId で直近発行分を強調）、
 * 案件内の発行済みPDFをまとめてダウンロードできる。サイドバーには載せず、
 * 発行フローと案件画面からのみ到達する。
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { casesApi, getDocTypeLabel, type DocumentSummary } from "../api.js";
import { StatusBadge } from "../components/StatusBadge.js";
import { Modal } from "../components/Modal.js";
import { Button } from "../components/ui/Button.js";
import { toast } from "../components/Toast.js";
import { downloadIssuedPdf } from "../lib/downloadPdf.js";
import { getRecentIssuedIds } from "../lib/recentIssues.js";
import type { EstimateApiError } from "../components/document/EstimateSyncCard.js";
import { useScreenView } from "../lib/analytics.js";
import { useTenantDocumentTypes } from "../hooks/useDocTypeProfession.js";

export function CasePdfsPage() {
  useScreenView("case_pdfs");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const navState = location.state as { justIssuedId?: string; fromInvoiceIssue?: boolean } | null;
  const justIssuedId = navState?.justIssuedId;
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // 請求書発行から遷移してきた場合の「続けて見積書を…」案内モーダル
  const [showEstimateGuide, setShowEstimateGuide] = useState(false);

  // fromInvoiceIssue はモーダルを1度だけ出すためのフラグ。
  // 読み取ったら history state から消す（リロード時の再表示防止。justIssuedId のバッジは残す）
  useEffect(() => {
    if (!navState?.fromInvoiceIssue) return;
    setShowEstimateGuide(true);
    navigate(location.pathname, { replace: true, state: { justIssuedId: navState.justIssuedId } });
  }, [navState?.fromInvoiceIssue, navState?.justIssuedId, location.pathname, navigate]);

  const { data: caseDetail, isLoading } = useQuery({
    queryKey: ["case", id],
    queryFn: () => casesApi.get(id!),
    enabled: !!id,
  });

  // 見積書↔請求書の同期状態（案内モーダルの文言・アクション切替に使用）
  const { data: estimateStatus } = useQuery({
    queryKey: ["case", id, "estimate-status"],
    queryFn: () => casesApi.estimateStatus(id!),
    enabled: !!id,
  });

  // 案内モーダルから「作成」「更新」を実行 → 見積書へ遷移（DocumentPage §6-2 と同じ挙動）
  const estimateGuideMut = useMutation({
    mutationFn: (mode: "create" | "refresh") => casesApi.estimateGenerate(id!, mode),
    onSuccess: (estimateDoc) => {
      qc.invalidateQueries({ queryKey: ["case", id] });
      qc.invalidateQueries({ queryKey: ["case", id, "estimate-status"] });
      qc.invalidateQueries({ queryKey: ["document", estimateDoc.id] });
      setShowEstimateGuide(false);
      // 見積書側の「← 請求書に戻る」導線には発行した請求書を渡す
      navigate(`/documents/${estimateDoc.id}`, justIssuedId ? { state: { fromDocumentId: justIssuedId } } : undefined);
    },
    onError: (e: EstimateApiError) => {
      qc.invalidateQueries({ queryKey: ["case", id] });
      qc.invalidateQueries({ queryKey: ["case", id, "estimate-status"] });
      if (e.body?.error?.code === "ESTIMATE_DRAFT_EXISTS") {
        toast("既存の下書きがあります。表示を最新の状態に更新しました。", "error");
      } else {
        toast(e.message, "error");
      }
    },
  });

  async function handleDownload(d: DocumentSummary) {
    setDownloadingId(d.id);
    try {
      const filename = await downloadIssuedPdf(d.id, d.doc_number);
      toast(`${filename} をダウンロードしました`);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setDownloadingId(null);
    }
  }

  // カスタム書類種別（tenant_document_types）のラベル解決（reviewer 指摘対応）
  const { data: docTypesData } = useTenantDocumentTypes();
  function docTypeLabel(docType: string): string {
    return docTypesData?.data.find((d) => d.doc_type === docType)?.label ?? getDocTypeLabel(docType);
  }

  if (isLoading) return <div style={{ padding: 24, color: "#94a3b8" }}>読み込み中…</div>;
  if (!caseDetail) return <div style={{ padding: 24, color: "#dc2626" }}>案件が見つかりません</div>;

  // このセッションで直近30分以内に発行した書類（新しい順）。
  // 請求書→見積書と連続発行した場合も全部に「いま発行」を付ける。
  const recentIds = getRecentIssuedIds(caseDetail.id);
  const recentRank = new Map(recentIds.map((docId, i) => [docId, i]));
  const isRecent = (docId: string) => recentRank.has(docId) || docId === justIssuedId;

  // 発行済みのみ・直近発行分を先頭（発行の新しい順）に、あとは発行日の新しい順
  const issuedDocs = (caseDetail.documents ?? [])
    .filter((d) => d.status === "issued")
    .sort((a, b) => {
      const ra = recentRank.get(a.id) ?? (a.id === justIssuedId ? -1 : Infinity);
      const rb = recentRank.get(b.id) ?? (b.id === justIssuedId ? -1 : Infinity);
      if (ra !== rb) return ra - rb;
      return (b.issued_at ?? "").localeCompare(a.issued_at ?? "");
    });

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      {/* パンくず */}
      <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>
        <Link to="/clients" style={{ color: "#0f766e", textDecoration: "none" }}>顧客管理</Link>
        {" › "}
        <Link to={`/clients/${caseDetail.client_id}`} style={{ color: "#0f766e", textDecoration: "none" }}>
          {caseDetail.client_name}
        </Link>
        {" › "}
        <Link to={`/cases/${caseDetail.id}`} style={{ color: "#0f766e", textDecoration: "none" }}>
          {caseDetail.title ?? `令和${caseDetail.fiscal_year}年 案件`}
        </Link>
        {" › 発行済みPDF"}
      </div>

      {/* 発行完了バナー（発行フローから遷移してきた場合のみ） */}
      {justIssuedId && (
        <div style={{ background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: "#0f172a" }}>発行が完了しました</div>
          <div style={{ fontSize: 12, color: "#134e4a", marginTop: 3 }}>
            発行した書類のPDFをダウンロードできます。「いま発行」の付いた書類が今回発行したものです。
          </div>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h1 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>発行した書類のダウンロード</h1>
        <Button variant="outline" onClick={() => navigate(`/cases/${caseDetail.id}`)}>← 案件画面に戻る</Button>
      </div>

      {issuedDocs.length === 0 ? (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "32px 24px", textAlign: "center", color: "#94a3b8" }}>
          この案件にはまだ発行済みの書類がありません。
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc", color: "#64748b", fontSize: 12 }}>
                <th style={{ textAlign: "left", padding: "10px 16px" }}>書類番号</th>
                <th style={{ textAlign: "left", padding: "10px 8px" }}>種別</th>
                <th style={{ textAlign: "left", padding: "10px 8px" }}>ステータス</th>
                <th style={{ textAlign: "left", padding: "10px 8px" }}>発行日</th>
                <th style={{ padding: "10px 16px" }} />
              </tr>
            </thead>
            <tbody>
              {issuedDocs.map((d) => {
                const isJustIssued = isRecent(d.id);
                return (
                  <tr
                    key={d.id}
                    style={{
                      borderTop: "1px solid #f1f5f9",
                      background: isJustIssued ? "#f0fdfa" : undefined,
                    }}
                  >
                    <td style={{ padding: "12px 16px", fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap" }}>
                      {d.doc_number ?? "（番号なし）"}
                      {d.version > 1 && (
                        <span style={{ marginLeft: 6, fontSize: 11, background: "#fef9c3", color: "#854d0e", padding: "1px 6px", borderRadius: 99, fontWeight: 600 }}>
                          v{d.version}
                        </span>
                      )}
                      {isJustIssued && (
                        <span style={{ marginLeft: 6, fontSize: 11, background: "#0f766e", color: "#fff", padding: "1px 8px", borderRadius: 99, fontWeight: 700 }}>
                          いま発行
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "12px 8px", color: "#334155", whiteSpace: "nowrap" }}>
                      {docTypeLabel(d.doc_type)}
                    </td>
                    <td style={{ padding: "12px 8px" }}><StatusBadge status={d.status} /></td>
                    <td style={{ padding: "12px 8px", color: "#64748b", whiteSpace: "nowrap" }}>
                      {d.issued_at ? new Date(d.issued_at).toLocaleDateString("ja-JP") : "－"}
                    </td>
                    <td style={{ padding: "12px 16px", whiteSpace: "nowrap", textAlign: "right" }}>
                      <div style={{ display: "inline-flex", gap: 8 }}>
                        <Button
                          variant="outline"
                          style={{ minHeight: "auto", padding: "5px 12px", fontSize: 12 }}
                          onClick={() => navigate(`/documents/${d.id}`)}
                        >
                          開く
                        </Button>
                        <Button
                          variant="primary"
                          style={{ minHeight: "auto", padding: "5px 12px", fontSize: 12 }}
                          disabled={downloadingId === d.id}
                          onClick={() => handleDownload(d)}
                        >
                          {downloadingId === d.id ? "取得中…" : "PDFダウンロード"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 請求書発行直後の見積書案内モーダル（DocumentPage §6-2 から移設。発行のたびに1回だけ表示） */}
      {showEstimateGuide && estimateStatus &&
        (estimateStatus.sync === "stale" || estimateStatus.sync === "in_sync" || estimateStatus.sync === "not_created") && (
        <Modal title="請求書を発行しました" onClose={() => setShowEstimateGuide(false)}>
          <p style={{ margin: "0 0 16px", fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
            {estimateStatus.sync === "stale" &&
              "続けて見積書を更新しますか？見積書がこの請求書の内容とまだ一致していません。更新すると、お客様に渡す見積書（総額）と請求書が揃います。"}
            {estimateStatus.sync === "in_sync" &&
              "続けて見積書を開きますか？見積書は請求書の内容と一致しています。開いて発行すると PDF をダウンロードできます。"}
            {estimateStatus.sync === "not_created" &&
              "続けて見積書を作成しますか？この案件の見積書はまだ作成されていません。請求書の合算から自動作成します。"}
          </p>
          <p style={{ margin: "0 0 16px", fontSize: 11, color: "#94a3b8", lineHeight: 1.6 }}>
            発行した請求書のPDFは、この画面からダウンロードできます。見積書は案件画面の書類一覧からもいつでも開けます。
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button variant="outline" onClick={() => setShowEstimateGuide(false)}>あとで</Button>
            {estimateStatus.sync === "stale" && (
              <Button variant="primary" disabled={estimateGuideMut.isPending} onClick={() => estimateGuideMut.mutate("refresh")}>
                {estimateGuideMut.isPending ? "更新中…" : "見積書を更新して開く"}
              </Button>
            )}
            {estimateStatus.sync === "in_sync" && (
              <Button
                variant="primary"
                onClick={() => {
                  setShowEstimateGuide(false);
                  navigate(`/documents/${estimateStatus.estimate!.id}`, justIssuedId ? { state: { fromDocumentId: justIssuedId } } : undefined);
                }}
              >
                見積書を開く
              </Button>
            )}
            {estimateStatus.sync === "not_created" && (
              <Button variant="primary" disabled={estimateGuideMut.isPending} onClick={() => estimateGuideMut.mutate("create")}>
                {estimateGuideMut.isPending ? "作成中…" : "見積書を作成して開く"}
              </Button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
